import { useEffect, useRef, useState, useMemo } from "react";

// ── Geometry helpers ──────────────────────────────────────────────────────
const _toRad = (d) => (d * Math.PI) / 180;
const _M_PER_DEG = 111320;

// Great-circle distance in metres.
export const haversineM = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371000;
  const dLat = _toRad(b.lat - a.lat);
  const dLng = _toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(_toRad(a.lat)) * Math.cos(_toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Human distance: metres under 1 km, miles above.
export const fmtDistance = (m) => {
  if (m == null || !Number.isFinite(m)) return null;
  if (m < 1000) return `${Math.max(0, Math.round(m / 10) * 10)} m`;
  return `${(m / 1609.34).toFixed(1)} mi`;
};

const loc = (l) => (l && l.lat != null && l.lng != null ? { lat: l.lat, lng: l.lng } : null);

/**
 * Project point `p` onto the segment a→b using a local equirectangular frame
 * centred on `a`. Returns { perpM, t } where `perpM` is the perpendicular
 * distance in metres and `t∈[0,1]` is the fractional position along the segment.
 */
const projectToSegment = (p, a, b) => {
  if (!p || !a || !b) return { perpM: Infinity, t: 0 };
  const cosLat = Math.cos(_toRad(a.lat));
  const bx = (b.lng - a.lng) * cosLat * _M_PER_DEG;
  const by = (b.lat - a.lat) * _M_PER_DEG;
  const px = (p.lng - a.lng) * cosLat * _M_PER_DEG;
  const py = (p.lat - a.lat) * _M_PER_DEG;
  const len2 = bx * bx + by * by;
  let t = len2 > 0 ? (px * bx + py * by) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const cx = t * bx;
  const cy = t * by;
  return { perpM: Math.hypot(px - cx, py - cy), t };
};

/**
 * useTurnByTurn — a real client-side navigation progress engine.
 *
 * The backend returns the full leg `steps[]` (each with start_location /
 * end_location coordinates) for the driver's current leg (origin = live GPS,
 * re-fetched periodically). We do NOT trust the backend's `next_step` (it is
 * always step[0] = the departure instruction, e.g. "Head south on Egerton Dr",
 * which is why the banner looked "stuck"). Instead, every GPS tick we:
 *
 *   1. PROJECT the live driver position onto every step segment
 *      (start_location → end_location) and pick the step the driver is
 *      currently travelling along (smallest perpendicular distance).
 *   2. The ACTIVE MANEUVER is the turn at the END of that step — i.e. the
 *      *next* step's instruction ("Turn left onto Main St"), shown with the
 *      remaining distance to that turn point.
 *   3. Progress is MONOTONIC within a route payload: we never step backwards
 *      (prevents banner flicker when the route doubles back near itself), and
 *      reset to the start when a fresh route payload arrives.
 *
 * Returns the step to display in the banner plus the live distance to its
 * maneuver. Display/UX only — never touches route generation, dispatch,
 * lifecycle, fare or payment.
 */
export function useTurnByTurn(steps, driver) {
  const list = useMemo(() => (Array.isArray(steps) ? steps : []), [steps]);
  const [cur, setCur] = useState(0); // index of the step the driver is ON
  const sigRef = useRef("");

  // Reset progress when a new route payload arrives (length + first/last instr).
  const sig = `${list.length}|${list[0]?.instruction || ""}|${list[list.length - 1]?.instruction || ""}`;
  useEffect(() => {
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setCur(0);
    }
  }, [sig]);

  const dLat = driver?.lat;
  const dLng = driver?.lng;

  // Recompute the current step from the live position every tick.
  useEffect(() => {
    if (dLat == null || list.length === 0) return;
    const p = { lat: dLat, lng: dLng };

    // Find the nearest step segment AT OR AHEAD of our current progress so we
    // never jump backwards on a self-overlapping route.
    let bestIdx = cur;
    let bestPerp = Infinity;
    for (let i = cur; i < list.length; i++) {
      const a = loc(list[i]?.start_location);
      const b = loc(list[i]?.end_location) || a;
      if (!a) continue;
      const { perpM } = projectToSegment(p, a, b);
      if (perpM < bestPerp) {
        bestPerp = perpM;
        bestIdx = i;
      }
    }

    // Advance once the driver is clearly past the end of the current step. We
    // use a generous ~30 m gate (GPS noise + road-snap offset) AND segment
    // projection, so it triggers reliably at driving speed without needing a
    // pinpoint pass over the exact corner.
    const a = loc(list[cur]?.start_location);
    const b = loc(list[cur]?.end_location) || a;
    if (a && b) {
      const { t } = projectToSegment(p, a, b);
      const distToEnd = haversineM(p, b);
      const passed = t >= 0.85 || (distToEnd != null && distToEnd < 30);
      if (passed && cur < list.length - 1) {
        setCur(cur + 1);
        return;
      }
    }
    if (bestIdx > cur) setCur(bestIdx);
  }, [dLat, dLng, list, cur]);

  // Banner shows the maneuver at the END of the current step = the NEXT step's
  // instruction. On the final step there is no "next", so we show the arrival
  // instruction of the last step itself.
  const onLast = cur >= list.length - 1;
  const displayIdx = onLast ? list.length - 1 : cur + 1;
  const step = list[displayIdx] || list[cur] || null;
  // The maneuver AFTER the upcoming one — drives the "then …" preview line so
  // the driver sees current + upcoming maneuvers simultaneously (like Google).
  const thenStep = onLast ? null : (list[displayIdx + 1] || null);

  // The point the driver is heading toward for this maneuver: the end of the
  // current step (= start of the displayed turn). Distance is straight-line
  // driver→turn, which is accurate at the short ranges that matter.
  const turnPoint = loc(list[cur]?.end_location) || loc(step?.start_location);
  const distanceM =
    dLat != null && turnPoint
      ? haversineM({ lat: dLat, lng: dLng }, turnPoint)
      : step?.distance_meters ?? null;

  // ── Route snapping + progress (diagnostics + smooth guidance) ────────────
  // Project the live GPS onto the CURRENT step segment to get (a) the snapped
  // on-route point and (b) the fraction `t` travelled along this step. Raw GPS
  // is preserved by the caller; this snapped value is for guidance/diagnostics.
  let snapped = null;
  let segT = 0;
  if (dLat != null && list.length) {
    const a = loc(list[cur]?.start_location);
    const b = loc(list[cur]?.end_location) || a;
    if (a && b) {
      const { t } = projectToSegment({ lat: dLat, lng: dLng }, a, b);
      segT = t;
      snapped = { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
    }
  }

  // Route progress %: cumulative metres completed / total leg metres.
  let totalM = 0;
  let doneM = 0;
  for (let i = 0; i < list.length; i++) {
    const d = Number(list[i]?.distance_meters) || 0;
    totalM += d;
    if (i < cur) doneM += d;
    else if (i === cur) doneM += d * segT;
  }
  const progressPct = totalM > 0 ? Math.max(0, Math.min(100, Math.round((doneM / totalM) * 100))) : null;

  return {
    step,
    thenStep,
    currentStep: list[cur] || null,
    index: displayIdx,
    stepIndex: cur,
    total: list.length,
    distanceM,
    distanceText: fmtDistance(distanceM) || step?.distance_text || null,
    snapped,
    progressPct,
    segmentId: list.length ? `step-${cur}/${list.length - 1}` : null,
    arrived: onLast && distanceM != null && distanceM < 35,
  };
}
